import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { showToast } from '@/shared/utils/showToast';
import {
  fetchAdminAffiliate,
  fetchAdminAffiliateSummary,
  fetchAdminAffiliates,
  fetchAdminWithdrawals,
  recordAdminPayout,
} from '../apis/adminAffiliate';
import type { AdminAffiliateParams } from '../types/affiliate.types';

const AFFILIATES_KEY = ['admin-affiliates'];

/** Affiliate total cards. */
export const useAdminAffiliateSummary = () =>
  useQuery({ queryKey: [...AFFILIATES_KEY, 'summary'], queryFn: fetchAdminAffiliateSummary });

/** Daftar Anggota; the previous page stays on screen while the next one loads. */
export const useAdminAffiliates = (params: AdminAffiliateParams) =>
  useQuery({
    queryKey: [...AFFILIATES_KEY, 'list', params],
    queryFn: () => fetchAdminAffiliates(params),
    placeholderData: keepPreviousData,
  });

/** Detail Anggota; nothing is requested without an id. */
export const useAdminAffiliate = (affiliateId: string | undefined) =>
  useQuery({
    queryKey: [...AFFILIATES_KEY, 'detail', affiliateId],
    queryFn: () => fetchAdminAffiliate(affiliateId as string),
    enabled: Boolean(affiliateId),
  });

/** Riwayat of one affiliate; asked for only while it is shown. */
export const useAdminWithdrawals = (affiliateId: string, enabled = true) =>
  useQuery({
    queryKey: [...AFFILIATES_KEY, 'withdrawals', affiliateId],
    queryFn: () => fetchAdminWithdrawals(affiliateId),
    enabled,
  });

/** Sudah Transfer. Every affiliate view is refreshed either way: the balance changed, or it had already
 *  changed (409) and the new amount has to be shown. */
export const useRecordAdminPayout = (affiliateId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ amount, proof }: { amount: string; proof: File }) =>
      recordAdminPayout(affiliateId, amount, proof),
    onSuccess: () =>
      showToast({
        type: 'success',
        title: 'Transfer dicatat',
        message: 'Bukti transfer diunggah.',
      }),
    onError: (error) =>
      showToast({ type: 'error', title: 'Gagal mencatat transfer', message: error.message }),
    onSettled: () => queryClient.invalidateQueries({ queryKey: AFFILIATES_KEY }),
  });
};
